import { useState, useEffect, useCallback } from 'react';
import { dashboardService, AdminDashboardStats, HostDashboardStats, GuestDashboardStats, RecentActivity, PendingApprovals } from '../services/dashboardService';
import { useAppSelector } from '../store/hooks';
import { logError } from '../utils/errorLogger';

export interface UseDashboardDataReturn {
  data: AdminDashboardStats | HostDashboardStats | GuestDashboardStats | null;
  activity: RecentActivity | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch dashboard data based on user role
 */
export const useDashboardData = (): UseDashboardDataReturn => {
  const { user } = useAppSelector((state) => state.auth);
  const [data, setData] = useState<AdminDashboardStats | HostDashboardStats | GuestDashboardStats | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use active_role if available, otherwise fall back to primary role
      const activeRole = user.active_role || user.role;
      
      // Fetch dashboard stats and activity in parallel
      const [statsData, activityData] = await Promise.all([
        dashboardService.getDashboardStats(activeRole as 'admin' | 'host' | 'guest' | 'usher'),
        dashboardService.getRecentActivity()
      ]);

      setData(statsData);
      setActivity(activityData);
    } catch (err) {
      logError('Error fetching dashboard data', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    activity,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Custom hook for pending approvals (admin only)
 */
export const usePendingApprovals = () => {
  const [data, setData] = useState<PendingApprovals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const approvals = await dashboardService.getPendingApprovals();
      setData(approvals);
    } catch (err) {
      logError('Error fetching pending approvals', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Custom hook specifically for admin dashboard
 */
export const useAdminDashboard = () => {
  const [data, setData] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await dashboardService.getAdminStats();
      setData(stats);
    } catch (err) {
      logError('Error fetching admin dashboard data', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Custom hook specifically for host dashboard
 */
export const useHostDashboard = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [data, setData] = useState<HostDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    // Check if user has host role (either as primary role, in roles array, or as active_role)
    // This supports multi-role users (e.g., ushers who also have host role)
    // Note: Backend will do the final authorization check based on database roles
    const hasHostRole = (
      user?.role === 'host' ||
      user?.roles?.includes('host') ||
      user?.active_role === 'host'
    );
    
    if (!hasHostRole) {
      setLoading(false);
      setError('Access denied: Not a host');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const stats = await dashboardService.getHostStats();
      setData(stats);
    } catch (err) {
      logError('Error fetching host dashboard data', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch host dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user?.active_role, user?.role, user?.roles]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Custom hook specifically for guest dashboard
 */
export const useGuestDashboard = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [data, setData] = useState<GuestDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    // Check if user's active role is guest before fetching
    const activeRole = user?.active_role || user?.role;
    if (activeRole !== 'guest') {
      setLoading(false);
      setError('Access denied: Not a guest');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const stats = await dashboardService.getGuestStats();
      setData(stats);
    } catch (err) {
      logError('Error fetching guest dashboard data', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch guest dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user?.active_role, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Custom hook for recent activity
 */
export const useRecentActivity = () => {
  const [data, setData] = useState<RecentActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const activity = await dashboardService.getRecentActivity();
      setData(activity);
    } catch (err) {
      logError('Error fetching recent activity', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch recent activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};
